'use client';
import { parse, isAfter, isBefore } from 'date-fns';
import { tz } from '@date-fns/tz';

/**
 * Checks if the current moment (converted to the given `timeZone`) is within the specified date range.
 *
 * @param startDate Start date & time string in the format `YYYY-MM-DD HH:mm:ss` **in the same `timeZone`**.
 *                  Example: `'2025-10-01 00:00:00'`.
 * @param endDate   End date & time string in the format `YYYY-MM-DD HH:mm:ss` **in the same `timeZone`**.
 *                  Example: `'2025-10-31 23:59:59'`.
 * @param timeZone  IANA time-zone string, e.g. `'Asia/Singapore'`, `'America/New_York'`.
 * @param pattern   Optional custom date-fns parse pattern. Defaults to `'yyyy-MM-dd HH:mm:ss'`.
 * @returns         `true` if current time is between startDate and endDate (inclusive), `false` otherwise.
 */
export const isWithinDateRangeInTimeZone = (
  startDate: string,
  endDate: string,
  timeZone: string,
  pattern = 'yyyy-MM-dd HH:mm:ss',
): boolean => {
  // Parse start and end dates in the specified time-zone context
  const startDateTime = parse(startDate, pattern, new Date(), { in: tz(timeZone) });
  const endDateTime = parse(endDate, pattern, new Date(), { in: tz(timeZone) });

  const now = new Date();

  // Check if current time is after start date AND before end date
  return isAfter(now, startDateTime) && isBefore(now, endDateTime);
};

/**
 * Checks if the current moment is within the specified date range (inclusive on both ends).
 * This version includes the exact start and end moments.
 *
 * @param startDate Start date & time string
 * @param endDate   End date & time string
 * @param timeZone  IANA time-zone string
 * @param pattern   Optional custom date-fns parse pattern
 * @returns         `true` if current time is between startDate and endDate (inclusive), `false` otherwise.
 */
export const isWithinDateRangeInclusiveInTimeZone = (
  startDate: string,
  endDate: string,
  timeZone: string,
  pattern = 'yyyy-MM-dd HH:mm:ss',
): boolean => {
  const startDateTime = parse(startDate, pattern, new Date(), { in: tz(timeZone) });
  const endDateTime = parse(endDate, pattern, new Date(), { in: tz(timeZone) });

  const now = new Date();

  // Use >= and <= for inclusive comparison
  return (now >= startDateTime || isAfter(now, startDateTime)) && (now <= endDateTime || isBefore(now, endDateTime));
};
